import { BASE_URL } from "../constants/APIConstants";
import { apiRequest, deleteResource, getResource } from "../api/http";
import { decryptPayload, encryptPayload } from "../utils/crypto";

// Most user endpoints return an encrypted payload in `data.data`.
const decrypted = (data) => decryptPayload(data.data);

export function getUser(user_id) {
  return getResource(`/api/user/details/${user_id}`, { select: decrypted });
}

/**
 * App activity of a user: `{ lastActiveAt, days, activeDays, trackedSince }`, `activeDays`
 * being the days (UTC) the app was used over the last `days` days. `null` on failure.
 */
export function getUserActivity(user_id, days = 90) {
  return getResource(`/api/user/activity/${user_id}?days=${days}`, {
    select: (data) => data,
    fallback: null,
  });
}

/**
 * Every user at once (heavy: only loaded on demand). Rejects on failure so
 * the page can tell it apart from an empty list.
 */
export function getUserList() {
  return getResource("/api/user/list", {
    select: (data) => {
      const users = decrypted(data);
      if (!Array.isArray(users)) throw new Error("Unexpected user list");
      return users;
    },
    rethrow: true,
  });
}

/** Search part of the paginated user list URL (email, phone or name). */
export const userSearchQuery = (value) => {
  if (!value.trim()) return "";
  if (value.includes("@")) return `?email=${encodeURIComponent(value)}`;
  if (/^\d+/.test(value)) return `?phone=${encodeURIComponent(value)}`;
  if (value.includes(" "))
    return `?last_name=${encodeURIComponent(value.split(" ")[1] || value)}`;
  return `?name=${encodeURIComponent(value)}`;
};

export function getUserListCount() {
  return getResource("/api/user/list-count", {
    select: decrypted,
    fallback: [],
  });
}

export function getUserListByPageWithSearch(page, searchQuery = "") {
  return getResource(`/api/user/list/${page}${searchQuery}`, {
    select: decrypted,
    fallback: [],
  });
}

export function getUserListByPage(page) {
  return getUserListByPageWithSearch(page);
}

export function getUsersFreeCount() {
  return getResource("/api/user/free-count", {
    select: decrypted,
    fallback: [],
  });
}

export function getUsersActivesCount() {
  return getResource("/api/user/active", {
    select: (data) => data.data,
    fallback: [],
  });
}

/**
 * App activity counters: `{ active, everActive }`, `active` being the users seen over the
 * last `days` days and `everActive` those with any recorded activity. `null` on failure.
 */
export function getUsersActivityCounts(days = 30) {
  return getResource(`/api/user/active-recently?days=${days}`, {
    select: (data) => ({ active: data.data, everActive: data.everActive }),
    fallback: null,
  });
}

/**
 * Active users over the last `days` days compared with the `days` days before:
 * `{ days, current, previous, change, complete, trackedSince }`, `change` being the evolution
 * in % (`null` when the previous period is empty) and `complete` false while the activity
 * history does not cover the previous period yet. `null` on failure.
 */
export function getActiveUsersEvolution(days = 30) {
  return getResource(`/api/user/active-evolution?days=${days}`, {
    select: (data) => data,
    fallback: null,
  });
}

export function updateUserDetails(obj) {
  return apiRequest("put", "/api/user", {
    data: encryptPayload(obj),
    select: decrypted,
  });
}

export function deleteUser(user_id) {
  return deleteResource(`/api/user/${user_id}`);
}

/**
 * Real subscription of a user (not only the `user.subscription` flag): `{ state, isActive,
 * userFlag, subscription: { provider, providerLabel, type, status, start, end, cancelAtPeriodEnd,
 * stripeSubscriptionId } | null, payments }`, `state` being none / active / canceling / past_due /
 * expired. Resolves with `null` on failure.
 */
export function getUserSubscriptionDetails(userId) {
  return getResource(`/api/admin/users/${userId}/subscription`, {
    select: (data) => data.data,
    fallback: null,
  });
}

/**
 * Removes the subscription right away (premium access ends, Stripe billing stops).
 * Resolves with `{ message, data }` (data: see getUserSubscriptionDetails); rejects with an
 * Error carrying the API message.
 */
export async function revokeUserSubscription(userId) {
  try {
    return await apiRequest("post", `/api/admin/users/${userId}/revokeSubscription`, {
      rethrow: true,
    });
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Impossible de retirer l'abonnement"
    );
  }
}

/**
 * Grants a subscription (`subscriptionType`: "monthly", "free"...) to a user.
 * Resolves with the response body; rejects with an Error carrying the API message.
 */
export async function giveUserSubscription(userId, subscriptionType, months) {
  const response = await fetch(
    `${BASE_URL}/api/admin/users/${userId}/giveSubscription`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ subscriptionType, months }),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(
      data.message || "Erreur lors de la modification de l'abonnement"
    );
  }
  return data;
}

/**
 * Everything a user did: payments (net totals), engagement, last videos
 * watched, last comments, quiz, exclusive content, journal. Rejects on failure.
 */
export function getUserOverview(user_id) {
  return getResource(`/api/admin/users/${user_id}/overview`, { rethrow: true });
}
